import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import getSelectedTextElementObject from 'editor/src/store/design/selector/getSelectedTextElementObject';
import { TextWrapping } from 'editor/src/store/design/types';
import applyFontStylesToSelectionOperation from 'editor/src/store/fonts/operation/applyFontStylesToSelectionOperation';
import getCurrentFontStylesProperty from 'editor/src/store/fonts/selector/getCurrentFontStylesProperty';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import Checkbox from '../Checkbox';
import IconInfo from '../Icon/IconInfo';
import SwitchControl from '../SwitchControl';
import WithTooltip from '../WithTooltip';

function TextFittingControl() {
  const wrapping = useSelector((state) => getCurrentFontStylesProperty(state, 'wrapping')) ?? TextWrapping.Wrap;
  const lockBoxSize = useSelector((state) => !!getSelectedTextElementObject(state)?.extra?.lockBoxSize);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const onFitSwitch = useCallback(
    (on: boolean) => {
      dispatch(
        applyFontStylesToSelectionOperation({
          wrapping: on ? TextWrapping.Fit : undefined,
        }),
      );
    },
    [wrapping],
  );

  const onMultiLineSwitch = useCallback(
    (on: boolean) => {
      dispatch(
        applyFontStylesToSelectionOperation({
          wrapping: on ? TextWrapping.Fit : TextWrapping.FitOneLine,
        }),
      );
    },
    [wrapping],
  );

  const autoFitElt = useMemo(
    () => (
      <>
        {t('Autofit text')}
        <WithTooltip overlay={t('editor-autofit-tooltip')}>
          <IconInfo size="small" className="ml-1" />
        </WithTooltip>
      </>
    ),
    [],
  );

  return (
    <>
      <SwitchControl
        title={autoFitElt}
        on={wrapping === TextWrapping.Fit || wrapping === TextWrapping.FitOneLine}
        onSwitch={onFitSwitch}
        className="mb-2"
        disabled={lockBoxSize}
      />
      <Checkbox
        disabled={lockBoxSize || wrapping === TextWrapping.Wrap}
        on={wrapping !== TextWrapping.FitOneLine}
        label={t('editor-allow-multiline')}
        onChange={onMultiLineSwitch}
      />
    </>
  );
}

export default React.memo(TextFittingControl);
